import React from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Ban, Loader2, Mail } from "lucide-react";
import { CustomDocName } from "@/components/application/CustomDocName";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatDateTime } from "@/utils/dateUtils";
import { staffFlowStepLabel } from "../checklistLabels";
import { FlowStepper, OnThePage } from "../primitives";
import type { FlowDetail } from "../types";
import CopyFlowLinkButton from "./CopyFlowLinkButton";
import { deliveryFailures } from "./deliveryFailures";
import DownloadLinkList from "./DownloadLinkList";
import FlowDocuments from "./FlowDocuments";
import SentDocumentButton from "./SentDocumentButton";
import { flowActionErrorMessage } from "./flowActionError";
import { flowDetailQuery, liveFlowRefetch } from "./queries";
import { FLOW_STATUS_META, STATUS_TONE_CLASSES, flowLabel, sentByPhrase } from "./status";
import { invalidateConsequence, useFlowActions } from "./useFlowActions";

export interface FlowDetailModalProps {
  borrowerId: number;
  /** Kept after close so the content stays put during the exit animation. */
  flowId: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Whether Resend invite and Invalidate link are offered (default true). False from a thread on
   * the picked fake model: resend emails the merchant and invalidate voids DocuSeal submissions.
   */
  outbound?: boolean;
}

/**
 * Radix unmounts the content when closed, so S6/S11 only load while the modal is open. An open
 * flow is re-read every 5 s meanwhile: its stepper and "On the page" follow the merchant live.
 */
const FlowDetailModal: React.FC<FlowDetailModalProps> = ({
  borrowerId,
  flowId,
  open,
  onOpenChange,
  outbound = true,
}) => (
  <Dialog open={open && flowId != null} onOpenChange={onOpenChange}>
    {/* Wide for the Documents table; on a phone, full width less a gutter. Scrolls inside. */}
    <DialogContent className="max-h-[90dvh] w-[calc(100vw-2rem)] max-w-3xl overflow-y-auto">
      {flowId != null ? (
        <FlowDetailBody key={flowId} borrowerId={borrowerId} flowId={flowId} outbound={outbound} />
      ) : null}
    </DialogContent>
  </Dialog>
);

function FlowDetailBody({
  borrowerId,
  flowId,
  outbound,
}: {
  borrowerId: number;
  flowId: number;
  outbound: boolean;
}) {
  const detail = useQuery({
    ...flowDetailQuery(borrowerId, flowId),
    refetchInterval: liveFlowRefetch,
  });
  const flow = detail.data;

  if (!flow) {
    return (
      <DialogHeader>
        <DialogTitle>Flow details</DialogTitle>
        <DialogDescription>
          {detail.isError ? (
            <span className="text-destructive">{flowActionErrorMessage(detail.error)}</span>
          ) : (
            "Loading…"
          )}
        </DialogDescription>
      </DialogHeader>
    );
  }

  const status = FLOW_STATUS_META[flow.status];
  const failures = deliveryFailures(flow);
  const isOpen = flow.status === "open";

  return (
    // A dialog grid item: min-w-0 so wide content scrolls or truncates, never widens the dialog.
    <div className="min-w-0 space-y-5">
      <DialogHeader>
        <div className="flex flex-wrap items-center gap-2 pr-6">
          <DialogTitle className="min-w-0 [overflow-wrap:anywhere]">{flowLabel(flow)}</DialogTitle>
          <Badge variant="outline" className={STATUS_TONE_CLASSES[status.tone].badge}>
            {status.label}
          </Badge>
          {flow.on_page ? <OnThePage /> : null}
        </div>
        <DialogDescription>
          {sentByPhrase(flow)} as {flow.sent_as} on {formatDateTime(flow.created_at)}
        </DialogDescription>
        {flow.flow_type === "one_off_contract" && (
          <Badge variant="outline" className="w-fit">
            Custom
          </Badge>
        )}
      </DialogHeader>

      {flow.steps.length ? (
        <section className="space-y-1.5">
          <h3 className="text-xs font-medium text-muted-foreground">Steps</h3>
          <FlowStepper
            steps={flow.steps}
            currentKey={isOpen ? (flow.current_step?.key ?? null) : null}
          />
        </section>
      ) : null}

      {flow.link_active ? (
        <section className="space-y-1.5">
          <h3 className="text-xs font-medium text-muted-foreground">Merchant link</h3>
          <div className="group relative">
            <code className="block select-all break-all rounded-md border bg-muted/40 px-2.5 py-1.5 pr-8 font-mono text-xs leading-relaxed">
              {flow.link_url}
            </code>
            <CopyFlowLinkButton borrowerId={borrowerId} flowId={flow.id} appearance="icon" />
          </div>
        </section>
      ) : null}

      <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1.5 text-sm">
        <dt className="text-muted-foreground">Recipient</dt>
        <dd className="break-all">{flow.recipient_email}</dd>
        {flow.has_merchant_link ? (
          <>
            <dt className="text-muted-foreground">Last emailed</dt>
            <dd>{formatDateTime(flow.last_sent_at)}</dd>
          </>
        ) : (
          <>
            {/* A document has no invite: its one email is the attached PDF. */}
            <dt className="text-muted-foreground">Emailed</dt>
            <dd>
              {flow.distribution.distributed_at
                ? formatDateTime(flow.distribution.distributed_at)
                : "Not yet"}
            </dd>
          </>
        )}
        {isOpen ? (
          <>
            <dt className="text-muted-foreground">Current step</dt>
            <dd>
              {!flow.current_step ? (
                "Not started"
              ) : flow.current_step.custom ? (
                <CustomDocName name={flow.current_step.label} focusable />
              ) : (
                staffFlowStepLabel(flow.current_step)
              )}
            </dd>
            <dt className="text-muted-foreground">Expires</dt>
            <dd>{formatDateTime(flow.expires_at)}</dd>
          </>
        ) : null}
      </dl>

      {failures.map((line) => (
        <Alert key={line} variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{line}</AlertDescription>
        </Alert>
      ))}

      {flow.document_count > 0 ? <FlowDocuments borrowerId={borrowerId} flowId={flow.id} /> : null}

      <FlowActions
        borrowerId={borrowerId}
        flow={flow}
        outbound={outbound}
        documentButton={
          flow.custom_upload ? (
            <SentDocumentButton upload={flow.custom_upload} document={!flow.has_merchant_link} />
          ) : null
        }
      />

      <DownloadLinkList
        borrowerId={borrowerId}
        flowId={flow.id}
        hasMerchantLink={flow.has_merchant_link}
      />
    </div>
  );
}

/**
 * Resend and invalidate, only while the link still opens and `outbound` (never on the picked fake);
 * a result or error outlives them.
 * `documentButton` (a custom upload's button) sits at the right of that row, or alone at the right.
 */
function FlowActions({
  borrowerId,
  flow,
  outbound,
  documentButton,
}: {
  borrowerId: number;
  flow: FlowDetail;
  outbound: boolean;
  documentButton: React.ReactNode;
}) {
  const {
    resend,
    invalidate,
    resendInvite,
    invalidateLink,
    confirmingInvalidate,
    setConfirmingInvalidate,
    failed,
    busy,
  } = useFlowActions(borrowerId, flow.id);

  return (
    <div className="space-y-3">
      {flow.link_active && outbound ? (
        <div data-testid="flow-actions" className="flex flex-wrap items-center gap-2 border-t pt-4">
          {confirmingInvalidate ? (
            <>
              <p className="w-full text-sm text-muted-foreground">{invalidateConsequence(flow)}</p>
              <Button
                type="button"
                size="sm"
                variant="destructive"
                disabled={busy}
                onClick={invalidateLink}
              >
                {invalidate.isPending ? <Loader2 className="animate-spin" /> : <Ban />}
                Invalidate link
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => setConfirmingInvalidate(false)}
              >
                Keep open
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={resendInvite}
              >
                {resend.isPending ? <Loader2 className="animate-spin" /> : <Mail />}
                Resend invite
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="text-destructive hover:text-destructive"
                disabled={busy}
                onClick={() => setConfirmingInvalidate(true)}
              >
                <Ban />
                Invalidate…
              </Button>
            </>
          )}
          {/* Right-aligned, on a wrapped line too. */}
          {documentButton ? <div className="ml-auto">{documentButton}</div> : null}
        </div>
      ) : documentButton ? (
        <div data-testid="flow-document-row" className="flex justify-end">
          {documentButton}
        </div>
      ) : null}

      {resend.isSuccess ? (
        <p role="status" className="text-sm text-muted-foreground">
          Invite resent to {resend.data.recipient_email}.
        </p>
      ) : null}
      {failed ? (
        <Alert variant="destructive">
          <AlertDescription>{flowActionErrorMessage(failed)}</AlertDescription>
        </Alert>
      ) : null}
    </div>
  );
}

export default FlowDetailModal;
